import React, { useCallback, useEffect, useState } from 'react';
import { FaCheckCircle, FaCog, FaPlus, FaRobot, FaSave, FaSyncAlt, FaTrash } from 'react-icons/fa';
import { useColumns } from '../../contexts/columnsContext';
import { useUser } from '../../contexts/userContext';
import api from '../../services/api';
import Header from '../Header';
import './WhatsappZapi.css';

const emptyForm = {
  id: null,
  name: '',
  scope: 'user',
  owner_user_id: '',
  phone: '',
  zapi_instance_id: '',
  zapi_token: '',
  zapi_client_token: '',
  default_column_id: '',
  default_origem: 'WhatsApp',
  send_policy: 'owner_only',
  notify_sent_by_me: true,
  mark_whatsapp_read_on_open: false,
  is_active: true,
};

const emptyAiForm = {
  configured: false,
  api_key: '',
  model: 'gpt-5.4-mini',
  tenant_monthly_limit: 0,
  user_monthly_limit: 0,
  updated_at: null,
};

function WhatsappZapiSettings() {
  const { user, listAllUsers, getAccessLevel } = useUser();
  const { columns } = useColumns();
  const [instances, setInstances] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [aiForm, setAiForm] = useState(emptyAiForm);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAiSettings, setLoadingAiSettings] = useState(false);
  const [savingAiSettings, setSavingAiSettings] = useState(false);
  const canConfigure = getAccessLevel ? getAccessLevel('adm') : false;

  const loadInstances = useCallback(async () => {
    try {
      const response = await api.get('/whatsapp-zapi/instances');
      setInstances(response.data || []);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Erro ao carregar instancias.');
    }
  }, []);

  const loadAiSettings = useCallback(async () => {
    if (!canConfigure) return;
    setLoadingAiSettings(true);
    try {
      const response = await api.get('/whatsapp-zapi/ai-settings');
      setAiForm({ ...emptyAiForm, ...(response.data || {}), api_key: '' });
    } catch (error) {
      setMessage(error.response?.data?.message || 'Erro ao carregar configuracao de IA.');
    } finally {
      setLoadingAiSettings(false);
    }
  }, [canConfigure]);

  useEffect(() => {
    loadInstances();
  }, [loadInstances]);

  useEffect(() => {
    loadAiSettings();
  }, [loadAiSettings]);

  const resetForm = () => {
    setForm({ ...emptyForm, owner_user_id: user?.id || '' });
  };

  const editInstance = (instance) => {
    setForm({
      id: instance.id,
      name: instance.name || '',
      scope: instance.scope || 'user',
      owner_user_id: instance.owner_user_id || '',
      phone: instance.phone_display || instance.phone_normalized || '',
      zapi_instance_id: instance.zapi_instance_id || '',
      zapi_token: '',
      zapi_client_token: '',
      default_column_id: instance.default_column_id || '',
      default_origem: instance.default_origem || 'WhatsApp',
      send_policy: 'owner_only',
      notify_sent_by_me: instance.notify_sent_by_me !== false,
      mark_whatsapp_read_on_open: instance.mark_whatsapp_read_on_open === true,
      is_active: instance.is_active !== false,
    });
  };

  const saveInstance = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const payload = {
        ...form,
        owner_user_id: form.owner_user_id || user?.id,
        default_column_id: form.default_column_id || null,
        send_policy: 'owner_only',
      };
      if (form.id) {
        await api.put(`/whatsapp-zapi/instances/${form.id}`, payload);
      } else {
        await api.post('/whatsapp-zapi/instances', payload);
      }
      await loadInstances();
      resetForm();
      setMessage('Instancia salva.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Erro ao salvar instancia.');
    } finally {
      setLoading(false);
    }
  };

  const testInstance = async (id) => {
    setMessage('');
    try {
      await api.post(`/whatsapp-zapi/instances/${id}/test`);
      await loadInstances();
      setMessage('Credenciais testadas com sucesso.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Erro ao testar instancia.');
    }
  };

  const configureWebhooks = async (id) => {
    setMessage('');
    try {
      const response = await api.post(`/whatsapp-zapi/instances/${id}/configure-webhooks`);
      const verification = response.data?.verification;
      await loadInstances();
      if (verification?.allConfigured) {
        setMessage('Webhooks atualizados e validados com sucesso na Z-API.');
      } else {
        const failedChecks = Object.entries(verification?.checks || {})
          .filter(([, configured]) => !configured)
          .map(([name]) => name)
          .join(', ');
        setMessage(`A Z-API nao confirmou todas as configuracoes${failedChecks ? `: ${failedChecks}` : ''}.`);
      }
    } catch (error) {
      setMessage(error.response?.data?.message || 'Erro ao configurar webhooks. Confira PUBLIC_API_BASE_URL HTTPS.');
    }
  };

  const reprocessWebhooks = async (id) => {
    setMessage('');
    try {
      const response = await api.post(`/whatsapp-zapi/instances/${id}/reprocess-webhooks`);
      const result = response.data || {};
      await loadInstances();
      setMessage(
        `Reprocessamento concluido: ${Number(result.processed || 0)} processados, `
        + `${Number(result.pending || 0)} pendentes e ${Number(result.failed || 0)} com falha.`,
      );
    } catch (error) {
      setMessage(error.response?.data?.message || 'Erro ao reprocessar webhooks.');
    }
  };

  const disableInstance = async (id) => {
    setMessage('');
    try {
      await api.delete(`/whatsapp-zapi/instances/${id}`);
      await loadInstances();
      setMessage('Instancia desativada.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Erro ao desativar instancia.');
    }
  };

  const saveAiSettings = async (event, clearApiKey = false) => {
    event?.preventDefault();
    setSavingAiSettings(true);
    setMessage('');
    try {
      const response = await api.put('/whatsapp-zapi/ai-settings', {
        api_key: aiForm.api_key,
        model: aiForm.model,
        tenant_monthly_limit: aiForm.tenant_monthly_limit,
        user_monthly_limit: aiForm.user_monthly_limit,
        clear_api_key: clearApiKey,
      });
      setAiForm({ ...emptyAiForm, ...(response.data || {}), api_key: '' });
      setMessage(clearApiKey ? 'Chave de IA removida.' : 'Configuracao de IA salva.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Erro ao salvar configuracao de IA.');
    } finally {
      setSavingAiSettings(false);
    }
  };

  return (
    <>
      <Header />
      <main className="zapi-whatsapp-settings-page" data-zapi-whatsapp>
        <section className="zapi-whatsapp-settings-header">
          <div>
            <h1>WhatsApp Z-API</h1>
            <p>Configure instancias, webhooks e credenciais de envio por usuario.</p>
          </div>
          <button type="button" onClick={resetForm}><FaPlus /> Nova instancia</button>
        </section>

        <section className="zapi-whatsapp-risk-box">
          <strong>Aviso importante</strong>
          <span>A Z-API nao e a API oficial da Meta/WhatsApp. Existe risco de instabilidade, bloqueio ou perda do numero se houver uso abusivo ou fora das politicas do WhatsApp.</span>
          <span>Textos ficam permanentes no CRM. Midias recebidas ficam apenas como link e podem expirar em ate 30 dias.</span>
        </section>

        {message && <div className="zapi-whatsapp-settings-message">{message}</div>}

        {canConfigure && (
          <section className="zapi-whatsapp-ai-settings-panel">
            <div className="zapi-whatsapp-ai-settings-header">
              <div>
                <h2><FaRobot /> IA para respostas</h2>
                <p>Chave OpenAI por empresa. A chave salva fica criptografada e nao e exibida novamente.</p>
              </div>
              <span className={aiForm.configured ? 'zapi-whatsapp-status-ok' : 'zapi-whatsapp-status-muted'}>
                {loadingAiSettings ? 'Carregando...' : aiForm.configured ? 'Chave configurada' : 'Sem chave'}
              </span>
            </div>

            <form className="zapi-whatsapp-ai-settings-form" onSubmit={saveAiSettings}>
              <label>OpenAI API Key {aiForm.configured ? '(preencha apenas para trocar)' : ''}</label>
              <input
                type="password"
                value={aiForm.api_key}
                onChange={(event) => setAiForm({ ...aiForm, api_key: event.target.value })}
                placeholder={aiForm.configured ? 'Chave ja configurada. Digite uma nova para trocar.' : 'sk-...'}
                autoComplete="new-password"
              />

              <label>Modelo</label>
              <input
                value={aiForm.model}
                onChange={(event) => setAiForm({ ...aiForm, model: event.target.value })}
                placeholder="gpt-5.4-mini"
              />

              <div className="zapi-whatsapp-ai-settings-limits">
                <label>
                  Limite mensal da empresa
                  <input
                    type="number"
                    min="0"
                    value={aiForm.tenant_monthly_limit}
                    onChange={(event) => setAiForm({ ...aiForm, tenant_monthly_limit: event.target.value })}
                  />
                </label>
                <label>
                  Limite mensal por usuario
                  <input
                    type="number"
                    min="0"
                    value={aiForm.user_monthly_limit}
                    onChange={(event) => setAiForm({ ...aiForm, user_monthly_limit: event.target.value })}
                  />
                </label>
              </div>

              <div className="zapi-whatsapp-ai-settings-actions">
                <button type="submit" disabled={savingAiSettings}><FaSave /> Salvar IA</button>
                {aiForm.configured && (
                  <button
                    type="button"
                    className="zapi-whatsapp-secondary-action"
                    onClick={() => saveAiSettings(null, true)}
                    disabled={savingAiSettings}
                  >
                    <FaTrash /> Remover chave
                  </button>
                )}
              </div>
            </form>
          </section>
        )}

        <div className="zapi-whatsapp-settings-grid">
          <section className="zapi-whatsapp-settings-list">
            <h2>Instancias cadastradas</h2>
            {instances.length === 0 && <p>Nenhuma instancia cadastrada.</p>}
            {instances.map((instance) => (
              <div key={instance.id} className="zapi-whatsapp-instance-item">
                <div>
                  <strong>{instance.name}</strong>
                  <span>{instance.phone_display || instance.phone_normalized || 'Numero nao informado'}</span>
                  <span>Dono: {instance.owner_username || instance.owner_user_id}</span>
                  <span>{instance.scope} - envio somente pelo dono</span>
                  <span>
                    Ultima mensagem recebida: {instance.last_message_webhook_at
                      ? new Date(instance.last_message_webhook_at).toLocaleString('pt-BR')
                      : 'nenhum webhook recebido'}
                  </span>
                  {Number(instance.pending_webhook_events || 0) > 0 && (
                    <span className="zapi-whatsapp-status-warning">
                      {instance.pending_webhook_events} evento(s) aguardando reprocessamento
                    </span>
                  )}
                  {instance.last_webhook_error && (
                    <span className="zapi-whatsapp-status-error">
                      Ultimo erro de webhook: {instance.last_webhook_error}
                    </span>
                  )}
                </div>
                <div className="zapi-whatsapp-instance-actions">
                  <span className={instance.connected ? 'zapi-whatsapp-status-ok' : 'zapi-whatsapp-status-muted'}>
                    {instance.connected ? 'Conectada' : 'Status indefinido'}
                  </span>
                  <button type="button" onClick={() => editInstance(instance)} title="Editar instancia"><FaCog /></button>
                  <button type="button" onClick={() => testInstance(instance.id)} title="Testar credenciais"><FaCheckCircle /></button>
                  <button
                    type="button"
                    className="zapi-whatsapp-instance-action-wide"
                    onClick={() => configureWebhooks(instance.id)}
                    title="Atualizar e validar webhooks na Z-API"
                  >
                    <FaSyncAlt /> Atualizar webhooks
                  </button>
                  <button
                    type="button"
                    className="zapi-whatsapp-instance-action-wide"
                    onClick={() => reprocessWebhooks(instance.id)}
                    title="Reprocessar eventos pendentes ou com falha"
                  >
                    <FaSyncAlt /> Reprocessar eventos
                  </button>
                  <button type="button" onClick={() => disableInstance(instance.id)} title="Desativar instancia"><FaTrash /></button>
                </div>
              </div>
            ))}
          </section>

          <section className="zapi-whatsapp-settings-form-panel">
            <h2>{form.id ? 'Editar instancia' : 'Nova instancia'}</h2>
            {!canConfigure && <div className="zapi-whatsapp-warning">Seu perfil pode nao ter permissao para configurar instancias.</div>}
            <form className="zapi-whatsapp-settings-form" onSubmit={saveInstance}>
              <label>Nome</label>
              <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />

              <label>Dono</label>
              <select
                value={form.owner_user_id}
                onChange={(event) => setForm({ ...form, owner_user_id: event.target.value })}
                disabled={!canConfigure}
              >
                <option value="">Usuario atual</option>
                {listAllUsers.map((item) => (
                  <option key={item.id} value={item.id}>{item.username}</option>
                ))}
              </select>

              <label>Escopo</label>
              <select value={form.scope} onChange={(event) => setForm({ ...form, scope: event.target.value })}>
                <option value="user">Usuario</option>
                <option value="tenant">Tenant/empresa</option>
              </select>

              <label>Telefone da instancia</label>
              <input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} placeholder="55..." />

              <label>Z-API Instance ID</label>
              <input value={form.zapi_instance_id} onChange={(event) => setForm({ ...form, zapi_instance_id: event.target.value })} required />

              <label>Z-API Token {form.id ? '(preencha so para trocar)' : ''}</label>
              <input value={form.zapi_token} onChange={(event) => setForm({ ...form, zapi_token: event.target.value })} required={!form.id} />

              <label>Client-Token {form.id ? '(preencha so para trocar)' : ''}</label>
              <input value={form.zapi_client_token} onChange={(event) => setForm({ ...form, zapi_client_token: event.target.value })} />

              <label>Coluna padrao para novos cards</label>
              <select value={form.default_column_id} onChange={(event) => setForm({ ...form, default_column_id: event.target.value })}>
                <option value="">Selecionar ao criar card</option>
                {columns.map((column) => (
                  <option key={column.id} value={column.id}>{column.name}</option>
                ))}
              </select>

              <label>Origem padrao</label>
              <input value={form.default_origem} onChange={(event) => setForm({ ...form, default_origem: event.target.value })} />

              <label>Politica de envio</label>
              <div className="zapi-whatsapp-policy-lock">
                Somente o dono da instancia consegue responder mensagens por este numero.
              </div>

              <label className="zapi-whatsapp-check-row">
                <input type="checkbox" checked={form.notify_sent_by_me} onChange={(event) => setForm({ ...form, notify_sent_by_me: event.target.checked })} />
                Refletir mensagens enviadas pelo celular no CRM
              </label>

              <label className="zapi-whatsapp-check-row">
                <input type="checkbox" checked={form.mark_whatsapp_read_on_open} onChange={(event) => setForm({ ...form, mark_whatsapp_read_on_open: event.target.checked })} />
                Marcar lida no WhatsApp ao abrir no CRM
              </label>

              <button type="submit" disabled={loading}><FaSave /> Salvar instancia</button>
            </form>
          </section>
        </div>
      </main>
    </>
  );
}

export default WhatsappZapiSettings;
